import { memo, useCallback, useMemo } from "react";

import { SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";
import { FormErrorMessage, FormItem, FormLabel } from "@/components/ui/form/_base/form";
import { RadioGroupField } from "@/components/ui/form/fields/radio-field/radio-group";
import { DatePickerRange } from "@/components/ui/input/date-picker-range";

import * as Constants from "./constants";
import { FormFieldLayout } from "../../layouts";

type Props = {
	// ディスカウントの利用可能期間
	saleTypeForTerm: SaleTypeForTerm;
	// 公開期間の開始日
	saleStart?: ForNodeType<string>;
	// 公開期間の終了日
	saleEnd?: ForNodeType<string>;
	// 利用可能期間の開始日
	stayableStart?: ForNodeType<string>;
	// 利用可能期間の終了日
	stayableEnd?: ForNodeType<string>;

	// ディスカウントの利用可能期間を設定するかの変更
	onChangeSaleTypeForTerm: (value: SaleTypeForTerm) => void;
	// 公開期間を変更
	onChangeSale: (value: DateRange) => void;
	// 利用可能期間を変更
	onChangeStayable: (value: DateRange) => void;
};

export const SaleTypeForTermFormFieldComposition = memo(
	({
		saleTypeForTerm,
		saleStart,
		saleEnd,
		stayableStart,
		stayableEnd,
		onChangeSaleTypeForTerm,
		onChangeSale,
		onChangeStayable,
	}: Props) => {
		/**
		 * 利用可能な期間を設定するかの変更
		 */
		const handleChangeSaleTypeForTerm = useCallback(
			(value: string) => {
				onChangeSaleTypeForTerm(value as SaleTypeForTerm);
			},
			[onChangeSaleTypeForTerm],
		);
		/**
		 * 公開期間を変更
		 */
		const handleChangeSale = useCallback(
			(dateRange: DateRange) => {
				onChangeSale(dateRange);
			},
			[onChangeSale],
		);
		/**
		 * 利用可能な期間の開始日を変更
		 */
		const handleChangeStayable = useCallback(
			(value: DateRange) => {
				onChangeStayable(value);
			},
			[onChangeStayable],
		);
		/**
		 * ディスカウントの利用可能期間に関する設定の表示
		 */
		const canShowLimitedOfSaleTypeForTerm = useMemo(
			() => saleTypeForTerm === SaleTypeForTerm.Limited,
			[saleTypeForTerm],
		);

		return (
			<FormFieldLayout title="利用可能な期間">
				<div className="ml-2">
					<RadioGroupField
						value={saleTypeForTerm}
						options={Constants.SaleTypeForTermOptions}
						handleValueChange={handleChangeSaleTypeForTerm}
					/>

					<div className="mt-2">
						<p className="whitespace-pre-wrap text-sm text-caption-50">
							{`このディスカウントが利用できる期間を設定しましょう。\n期間限定を選んだ場合、公開期間は、予約可能な期間。利用期間はチェックイン可能な期間を表しています。`}
						</p>
					</div>

					{canShowLimitedOfSaleTypeForTerm && (
						<div className="mt-5 space-y-3 pb-6">
							<FormItem>
								<FormLabel>公開期間</FormLabel>
								<DatePickerRange
									selected={{ from: saleStart?.value, to: saleEnd?.value }}
									onSelect={handleChangeSale}
								/>
								<FormErrorMessage>{saleStart?.error || saleEnd?.error}</FormErrorMessage>
							</FormItem>

							<FormItem>
								<FormLabel>利用可能な期間</FormLabel>

								<DatePickerRange
									selected={{ from: stayableStart?.value, to: stayableEnd?.value }}
									onSelect={handleChangeStayable}
								/>
								<FormErrorMessage>{stayableStart?.error || stayableEnd?.error}</FormErrorMessage>
							</FormItem>
						</div>
					)}
				</div>
			</FormFieldLayout>
		);
	},
);
